import React, { useState, useMemo } from 'react';
import { 
  Box,
  Text,
  Stack,
  Group,
  Paper,
  Table,
  Center,
  LoadingOverlay,
  ActionIcon,
  Tooltip,
  Button,
  Loader,
  Indicator,
  Progress,
} from '@mantine/core';
import { IconDatabaseOff, IconFilter, IconBrain } from '@tabler/icons';
import { showNotification } from '@mantine/notifications';
import { SourceIcon } from '../../../../../utils/SourceIcon';
import { 
  AvailableIntegration, 
  Integration, 
  ExternalObjectConf, 
  BagelObjectConf 
} from '@bageltechnologies/bagel-common';
import { MLActionFilter } from './MLActionFilter';
import { useMLProcessedCount, useSendMLItemUpdate } from '../../../../../hooks/useIntegrations';
import { useBagelObjects } from '../../../../../hooks/useBagelObjects';
import { useAuth0 } from '@auth0/auth0-react';

interface MLActionsTabProps {
  integration: {
    available: AvailableIntegration;
    connected?: Integration;
  };
  isLoading?: boolean;
  updateExternalObject: (originObject: string, updates: Partial<ExternalObjectConf>) => Promise<void>;
}

export const MLActionsTab: React.FC<MLActionsTabProps> = ({
  integration,
  isLoading = false,
  updateExternalObject
}) => {
  const { available, connected } = integration;
  const auth0 = useAuth0();
  // State for filter popup
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [currentFilterObject, setCurrentFilterObject] = useState<ExternalObjectConf | null>(null);
  
  const {
    data: bagelObjects,
    isLoading: isBagelObjectsLoading,
    isError: isBagelObjectsError,
  } = useBagelObjects(auth0);
  
  // Loading states for individual objects (like in MappingsTab)
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});

  // ML Item Update mutation
  const sendMLUpdate = useSendMLItemUpdate();

  // Get only enabled external objects that are mapped to bagel objects with ML Actions support
  const enabledExternalObjects = useMemo(() => {
    return connected?.externalObjects?.filter((obj) => {
      // Must be enabled
      if (obj.isDisabled === true) return false;
      
      // Must have a bagel object mapping
      if (!obj.bagelObjectKey) return false;
      
      // Must be mapped to a bagel object that supports ML Actions
      const matchingBagelObject = bagelObjects?.find(
        (bagelObj: BagelObjectConf) => bagelObj.key === obj.bagelObjectKey
      );
      
      return matchingBagelObject?.supportsMLActions === true;
    }) || [];
  }, [connected?.externalObjects, bagelObjects]);

  console.log('enabledExternalObjects', enabledExternalObjects);
  const handleOpenFilterModal = (externalObj: ExternalObjectConf) => {
    setCurrentFilterObject(externalObj);
    setFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
    setCurrentFilterObject(null);
  };

  const handleMLAction = async (externalObj: ExternalObjectConf) => {
    try {
      setLoadingStates(prev => ({ ...prev, [externalObj.originObject]: true }));
      
      const result = await sendMLUpdate.mutateAsync({
        key: available.key,
        originObjectKey: externalObj.originObject,
        updateData: {
          updateType: "updated",
          processAll: false,
          additionalData: {}
        }
      });

      showNotification({
        title: 'ML Action Completed',
        message: `Successfully processed ${result.processedCount} items for ${externalObj.label}`,
        color: 'green'
      });
    } catch (error) {
      console.error('ML Action failed:', error);
      showNotification({
        title: 'ML Action Failed',
        message: `Failed to process items for ${externalObj.label}`,
        color: 'red'
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [externalObj.originObject]: false }));
    }
  };

  const renderEmptyState = useMemo(() => (
    <Box py="xl">
      <Center>
        <Stack align="center" spacing="sm">
          <IconDatabaseOff size={40} color="#CED4DA" />
          <Text size="sm" color="dimmed">No ML-enabled objects available</Text>
          <Text size="xs" color="dimmed">
            There are no enabled {available.name} objects mapped to Bagel objects that support ML actions
          </Text>
        </Stack>
      </Center>
    </Box>
  ), [available.name]);

  // Component for rendering ML progress - memoized for performance
  const MLProgressCell = React.memo(({ externalObj }: { externalObj: ExternalObjectConf }) => {
    const { data: mlCounts, isLoading: isCountsLoading } = useMLProcessedCount(
      available.key,
      externalObj.originObject
    );

    if (isCountsLoading) {
      return <Loader size="xs" />;
    }

    if (!mlCounts) {
      return <Text size="xs" color="dimmed">No filters set</Text>;
    }

    const progressPercentage = mlCounts.totalMLEligible > 0 
      ? Math.round((mlCounts.mlProcessedCount / mlCounts.totalMLEligible) * 100)
      : 0;

    return (
      <Box style={{ minWidth: 120 }}>
        <Group spacing="xs" mb={4}>
          <Text size="xs">{mlCounts.mlProcessedCount} / {mlCounts.totalMLEligible}</Text>
          <Text size="xs" color="dimmed">({progressPercentage}%)</Text>
        </Group>
        <Progress
          value={progressPercentage}
          size="sm"
          color={progressPercentage === 100 ? "green" : progressPercentage > 0 ? "blue" : "gray"}
        />
      </Box>
    );
  });

  return (
    <Box pos="relative">
      {/* <LoadingOverlay visible={isLoading} overlayBlur={2} /> */}
      
      {/* ML Action Filter Modal */}
      {currentFilterObject && (
        <MLActionFilter
          opened={filterModalOpen}
          onClose={handleCloseFilterModal}
          externalObj={currentFilterObject}
          integrationKey={available.key}
          updateExternalObject={updateExternalObject}
        />
      )}
      
      <Stack spacing="xl">
        <Box>
          <Text weight={500} size="md">ML Actions</Text>
          <Text size="sm" color="dimmed" mb="xs">
            Configure ML actions on your {available.name} data, using filters to select which items to process.
          </Text>
        </Box>

        <Paper withBorder>
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th>{available.name} Objects</th>
                <th>Bagel Object</th>
                <th>Filter</th>
                <th>Progress</th>
                <th style={{ textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {enabledExternalObjects.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 0, border: 'none' }}>
                    {renderEmptyState}
                  </td>
                </tr>
              ) : (
                enabledExternalObjects.map((externalObj) => {
                  const matchingBagelObject = externalObj.bagelObjectKey ? 
                    { label: externalObj.bagelObjectKey } : null;
                  
                  // Get ML_events_filters count for indicator
                  const mlFiltersCount = (externalObj as any)?.ML_events_filters?.rules?.length || 0;
                  
                  return (
                    <tr key={externalObj.originObject}>
                      <td>
                        <Group spacing="sm">
                          {loadingStates[externalObj.originObject] ? (
                            <Loader size={20} />
                          ) : (
                            <SourceIcon sourceName={available.key} width={20} height={20} />
                          )}
                          <Box>
                            <Text>{externalObj.label}</Text>
                            {(externalObj.label !== externalObj.originObject) && 
                              <Text size="xs" color="dimmed">
                                {externalObj.originObject}
                              </Text>
                            }
                          </Box>
                        </Group>
                      </td>
                      <td>
                        {matchingBagelObject ? (
                          <Text>{matchingBagelObject.label}</Text>
                        ) : (
                          <Text size="xs" color="dimmed">No mapping</Text>
                        )}
                      </td>
                      <td>
                        <Tooltip label="Configure ML filters">
                          <ActionIcon
                            variant="outline"
                            color="indigo"
                            onClick={() => handleOpenFilterModal(externalObj)}
                            disabled={externalObj.isDisabled === true || loadingStates[externalObj.originObject] || isLoading}
                          >
                            <Indicator 
                              processing 
                              disabled={Boolean(!mlFiltersCount)} 
                              size={16} 
                              fz={8} 
                              color="indigo" 
                              label={mlFiltersCount}
                            >
                              <IconFilter size={16} />
                            </Indicator>
                          </ActionIcon>
                        </Tooltip>
                      </td>
                      <td>
                        <MLProgressCell externalObj={externalObj} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Button
                          variant="outline"
                          color="indigo"
                          leftIcon={<IconBrain size={16} />}
                          size="xs"
                          onClick={() => handleMLAction(externalObj)}
                          disabled={
                            externalObj.isDisabled === true || 
                            loadingStates[externalObj.originObject] || 
                            isLoading                          
                          }
                          loading={loadingStates[externalObj.originObject]}
                        >
                          Run ML Action
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Paper>
      </Stack>
    </Box>
  );
};

export default MLActionsTab; 